/**
 * App Manifest Types
 *
 * Defines the schema for app manifests that control how apps
 * are registered, loaded, and displayed in the Room platform.
 */

export interface AppManifestTile {
  label: string;
  section: 'main' | 'dock' | string;
  order: number;
  route: string;
  /** Role required to see this tile (e.g. "admin") */
  requiredRole?: string;
}

export interface AppManifestEntry {
  /** How this app is loaded */
  type: 'bundle' | 'pages';
  /** Path to bundle file (relative to app root) */
  bundle?: string;
  /** Cloudflare Pages project name */
  pagesProject?: string;
}

export interface AppManifest {
  /** Unique app identifier (lowercase, hyphens allowed) */
  id: string;
  /** Display name */
  name: string;
  /** Semantic version */
  version: string;
  /** App type */
  type: 'app' | 'game' | 'tool' | 'utility';
  /** Whether this is a core app or a dynamic (store) app */
  category: 'core' | 'dynamic';
  /** Short description */
  description: string;
  /** Emoji or icon URL */
  icon: string;
  /** Author identifier */
  author: string;
  /** Tile configuration (null = no tile shown on home screen) */
  tile: AppManifestTile | null;
  /** Entry point configuration */
  entry: AppManifestEntry;
  /** Required permissions */
  permissions: string[];
  /** Store categories for discovery */
  categories?: string[];
  /** Search tags */
  tags?: string[];
  /** SDXL prompt for AI icon generation (stored in DB) */
  mediaIconPrompt?: string;
  /** Fallback emoji shown while AI icon loads */
  mediaIconFallback?: string;
}

/**
 * Runtime representation of an app in the catalog.
 * Extends manifest with runtime state (installed, etc.)
 */
export interface AppCatalogEntry extends AppManifest {
  /** Whether the user has installed this app */
  installed: boolean;
  /** Bundle URL for loading */
  bundleUrl?: string;
}

/** List of core app IDs that are always available */
export const CORE_APP_IDS = [
  'home',
  'login',
  'chat',
  'store',
  'settings',
  'admin',
  'developer',
  'credits',
  'openclaw-gateway',
] as const;

export type CoreAppId = typeof CORE_APP_IDS[number];

/** Check if an app ID is a core app */
export function isCoreApp(appId: string): appId is CoreAppId {
  return (CORE_APP_IDS as readonly string[]).includes(appId);
}
